<script lang="ts">
	import { DashInfoCard, DashInfoWidget } from '@Shelly/shelly-design-system/index';
	import { push } from 'svelte-spa-router';
	import { real_id } from 'src/helpers/interface_helpers';
	import { defaultSelectedRoom } from 'src/helpers/constants';
	import { consumption_add_total, convert } from 'src/helpers/utilities';
	import { setSidePanelState } from 'src/components/Sidepanel/sidepanel-helpers';
	import { createMemberStatusSnapshot } from 'src/stores/device-runtime/status-selectors';
import { devices } from 'src/stores/lists/devices';
	import { devicesFilters } from 'src/stores/ui/filters';
	import { consumption_by_device_account } from 'src/stores/features/consumption';
	import type { Device } from 'src/types/Device';
	import { format } from 'svelte-i18n';
	import { sidepanelDesktopOnTop } from 'src/stores/ui/sidepanel-public';

	const units = 'W';
	const closeSidePanel = () => {
		if (!$sidepanelDesktopOnTop) {
			setSidePanelState({ open: false });
		}
	};

	const goToConsumption = () => {
		push('/cons/-1');
		closeSidePanel();
	};

	const goToAllDevices = () => {
		push('/home/devices/-1');
		devicesFilters.update((state) => ({
			...state,
			rooms: [defaultSelectedRoom],
		}));
		closeSidePanel();
	};

	const goToOffline = () => {
		push('/home/devices/-30');
		closeSidePanel();
	};

	const goToAllShared = () => {
		push('/home/devices/-40');
		closeSidePanel();
	};

	const goToRecentlyShared = () => {
		push('/home/devices/-20');
		closeSidePanel();
	};

	const goToHidden = () => {
		push('/home/devices/-10');
		closeSidePanel();
	};

	$: allDeviceIds = Object.keys($devices);
	$: memberStatusStore = createMemberStatusSnapshot(allDeviceIds);
	$: memberStatus = $memberStatusStore;
	$: includedPowerTotal = consumption_add_total($consumption_by_device_account, 'in_power');
	$: isGenerating = includedPowerTotal < 0;
	$: totalConsumption = convert(Math.abs(includedPowerTotal), units);
	$: devicesCount = Object.keys($devices).length.toString();
	$: offlineDevicesCount = Object.keys($devices)
		.filter((id) => !memberStatus[real_id(id)]?.online)
		.length.toString();
	$: sharedDevicesCount = Object.values($devices)
		.filter((d: Device) => d.shared && d.room_id === -20)
		.length.toString();
	$: sharedAllDevicesCount = Object.values($devices)
		.filter((d: Device) => d.shared)
		.length.toString();

	$: hiddenDevicesCount = Object.values($devices)
		.filter((d: Device) => d.room_id === -10)
		.length.toString();

	$: accInfoWidgetData = {
		totalConsumption,
		devicesCount,
		offlineDevicesCount,
		sharedDevicesCount,
		sharedAllDevicesCount,
		hiddenDevicesCount,
	};

	$: cards = [
		{
			icon: 'fal fa-bolt',
			value: accInfoWidgetData?.totalConsumption,
			infoText: isGenerating
				? $format('generating', { default: 'Generating' })
				: $format('label_consuming', { default: 'Consuming' }),
			_type: 'acc_info',
			id: 'consumption',
			color: isGenerating ? '--color-success' : '--color-grid',
			onClick: goToConsumption,
		},
		{
			icon: 'fa-light fa-microchip',
			value: accInfoWidgetData?.devicesCount,
			infoText: $format('label_all_devices', {
				default: 'All Devices',
			}),
			_type: 'acc_info',
			id: 'allDevices',
			color: '--color-secondary',
			onClick: goToAllDevices,
		},
		{
			icon: 'fa-light fa-cloud-slash text-color-muted',
			value: accInfoWidgetData?.offlineDevicesCount,
			infoText: $format('label_offline_devices', {
				default: 'Offline Devices',
			}),
			_type: 'acc_info',
			id: 'offlineDevices',
			color: '--color-muted',
			onClick: goToOffline,
		},
		{
			icon: 'fa-light fa-share-nodes',
			value: accInfoWidgetData?.sharedAllDevicesCount,
			infoText: $format('label_shared_devices', {
				default: 'Shared Devices',
			}),
			_type: 'acc_info',
			id: 'sharedDevices',
			color: '--color-primary',
			onClick: goToAllShared,
		},
		{
			icon: 'fa-light fa-clock-rotate-left',
			value: accInfoWidgetData?.sharedDevicesCount,
			infoText: $format('label_recently_shared', {
				default: 'Recently shared',
			}),
			_type: 'acc_info',
			id: 'recentlySharedDevices',
			color: '--color-secondary',
			onClick: goToRecentlyShared,
		},
		{
			icon: 'fa-light fa-eye-slash text-color-muted',
			value: accInfoWidgetData?.hiddenDevicesCount,
			infoText: $format('label_hidden_devices', {
				default: 'Hidden devices',
			}),
			_type: 'acc_info',
			id: 'hiddenDevices',
			color: '--color-muted',
			onClick: goToHidden,
		},
	];
</script>

<DashInfoWidget aria-label="List of Dash Info Cards">
	{#each cards as card}
		<DashInfoCard
			class="flex-1 min-width-0"
			icon={card.icon}
			value={card.value}
			infoText={card.infoText}
			color={card.color}
			isFocusable
			on:click={card.onClick}
		/>
	{/each}
</DashInfoWidget>
